import { navigateToUrl } from 'single-spa';
import { ParsedUserData, Account } from '../@types';
import api from './api';
import { adminActivityRoute, redirectAndRemoveLink } from '../utils/helper';

export const USER_PERMISSIONS_KEY = 'userperm';
export const USER_LABEL_PERMISSIONS_KEY = 'userlabels';

export const routeToHomeActivity = () => {
  // if the user is inside the /integrations/install or /silent-check-sso.html don't redirect to the home
  if (
    window.location.pathname.includes('integrations/install') ||
    window.location.pathname.includes('silent-check-sso.html')
  ) {
    return;
  }

  // check if there is a route to redirect available here, If yes, push to that route and ignore
  const redirectUrl = localStorage.getItem('redirect_uri');
  if (redirectUrl?.length > 0) {
    redirectAndRemoveLink();
  } else {
    navigateToUrl(adminActivityRoute);
  }
};

export const fetchUserData = async () => {
  let data;
  try {
    const { data: apiData } = await api.identityV2.get(
      '/users/self?withAccessPolicies=true'
    );
    data = apiData;
  } catch (err) {
    console.error('Error fetching user data:', err);
    window.dispatchEvent(
      new CustomEvent('onLoginFailure', { detail: err.message })
    );
    return;
  }
  const accounts = Object.values(data.accounts ?? {});

  const userData = {
    ...data,
    accounts,
  };

  window.sessionStorage.setItem('user', data.id);
  window.sessionStorage.setItem('userId', data.id);
  window.sessionStorage.setItem('firstName', data.firstName);
  window.sessionStorage.setItem('lastName', data.lastName);
  window.sessionStorage.setItem('email', data.email);

  const accountId = determineAccountId(userData);

  const userStateChangeEvent = new CustomEvent('onUserUpdate', {
    detail: {
      firstName: window.sessionStorage.getItem('firstName'),
      lastName: window.sessionStorage.getItem('lastName'),
      email: window.sessionStorage.getItem('email'),
      accountId: accountId,
    },
  });
  window.dispatchEvent(userStateChangeEvent);

  window.sessionStorage.setItem(
    'logoutRedirectUrl',
    data.logoutRedirectUrl ?? 'null'
  );
  localStorage.setItem('isLoggedInWithIdV2', 'true');
  if (!accounts.length) {
    sessionStorage.removeItem('accounts');
    navigateToUrl('/auth/no-account');
    return;
  }
  setUserDataOnSessionStorage(userData);
  if (data?.tncConsent?.consented) {
    routeToHomeActivity();
  } else {
    navigateToUrl('/auth/terms');
  }
};

export const setPermInLocalStorage = (data: Account) => {
  // set the current user permission into the localstorage
  window.localStorage.setItem(
    USER_PERMISSIONS_KEY,
    JSON.stringify(data.permissions || [])
  );
};

export const determineAccountId = (data: ParsedUserData) => {
  const lastSelectedAccountId = window.localStorage.getItem(
    'lastSelectedAccountId'
  );
  if (!data.accounts.length) return lastSelectedAccountId || '';
  return (
    data.accounts.find(account => account.id === lastSelectedAccountId)?.id ||
    data.accounts[0].id
  );
};

export function setUserDataOnSessionStorage(data: ParsedUserData) {
  window.sessionStorage.setItem('accounts', JSON.stringify(data.accounts));
  window.sessionStorage.setItem(
    'isStaffUserFederated',
    data.isStaffUserFederated ?? 'false'
  );
  window.sessionStorage.setItem('pimVersion', 'pim2');
  const lastSelectedAccountId = window.localStorage.getItem(
    'lastSelectedAccountId'
  );
  if (lastSelectedAccountId) {
    const lastSelectedAccount = data.accounts.find(
      account => account.id === lastSelectedAccountId
    );
    if (lastSelectedAccount) {
      window.sessionStorage.setItem('accountId', lastSelectedAccount.id);
      window.sessionStorage.setItem('accountName', lastSelectedAccount.name);
      setPermInLocalStorage(lastSelectedAccount);
      return;
    }
  }
  if (!data.accounts.length) return;
  window.sessionStorage.setItem('accountName', data.accounts[0].name);
  window.sessionStorage.setItem('accountId', data.accounts[0].id);
  window.localStorage.setItem('lastSelectedAccountId', data.accounts[0].id);

  setPermInLocalStorage(data.accounts[0]);
}
